import { FISCAL_MODALITIES, ISSUANCE_REGIMES } from '@efacture/fiscal-core';
import type { TranslationKey } from './billing-i18n';

/**
 * What an issued invoice is, read from its regime and its signing device.
 *
 * - `ordinary`: issued outside certification; it carries no certification
 *   mention whatever device is recorded.
 * - `certified`: signed by a certified MCF.
 * - `nonCertified`: signed by a simulator, a virtual MCF or an unknown
 *   device, as the PDF's « DOCUMENT NON CERTIFIÉ » mention states.
 * - `unrecorded`: no device recorded; the invoice asserts neither
 *   certification nor its absence, like its PDF.
 * - `provisional`: a « DOCUMENT PROVISOIRE » issued while no MCF could
 *   certify the sale; no device signed it and it is not a certified invoice.
 */
export type InvoiceIssueKind = 'ordinary' | 'certified' | 'nonCertified' | 'unrecorded' | 'provisional';

export interface InvoiceIssueStatus {
  kind: InvoiceIssueKind;
  labelKey: TranslationKey;
}

const NON_CERTIFIED_LABELS: Record<string, TranslationKey> = {
  [FISCAL_MODALITIES.SIMULATEUR]: 'invoices.issue.nonCertified.simulator',
  [FISCAL_MODALITIES.MCF_VIRTUEL]: 'invoices.issue.nonCertified.virtual',
  [FISCAL_MODALITIES.INCONNU]: 'invoices.issue.nonCertified.unknown',
};

/** Status of an issued invoice; a device outside `FISCAL_MODALITIES` reads as unknown. */
export function invoiceIssueStatusOf(invoice: {
  issuanceRegime?: string | null | undefined;
  fiscalModality?: string | null | undefined;
}): InvoiceIssueStatus {
  if (invoice.issuanceRegime === ISSUANCE_REGIMES.ORDINARY) {
    return { kind: 'ordinary', labelKey: 'invoices.issue.ordinary' };
  }
  if (invoice.issuanceRegime === ISSUANCE_REGIMES.PROVISIONAL) {
    return { kind: 'provisional', labelKey: 'provisional.badge' };
  }
  const modality = invoice.fiscalModality;
  if (modality === null || modality === undefined) {
    return { kind: 'unrecorded', labelKey: 'invoices.status.signed' };
  }
  if (modality === FISCAL_MODALITIES.MCF_CERTIFIE) {
    return { kind: 'certified', labelKey: 'invoices.issue.certified' };
  }
  return {
    kind: 'nonCertified',
    labelKey: NON_CERTIFIED_LABELS[modality] ?? 'invoices.issue.nonCertified.unknown',
  };
}
